import {
  AppUser,
  combineChildTimeAndParent,
  EventrDate,
  filterEntitiesToPastAndPresentWP,
  formatTime,
  getUserPref,
  getV2ValueWP,
  IEventrHotel,
  INextComponent,
  IRendererOptions,
  isDateInPast,
  ITodoList,
  SectionId,
  structuredCloneL,
  TPastCriteriaJoiner,
  tryParseJSON,
  TZoneTypes,
  V2,
} from "@eventr/common";

/** The calendar day of a date in the given zone. */
const dayOf = (date: EventrDate, zoneType: TZoneTypes) => {
  switch (zoneType) {
    case "userLocal":
      return date.toUserLocalDateString();
    case "itinLocal":
      return date.toItinLocalDateString();
    case "utc":
      return date.utcLuxon?.toISO()?.slice(0, 10);
  }
};

/** Wraps a read of a date string so each distinct string is read once. */
const perDateString = <T,>(read: (date: string) => T) => {
  const cache = new Map<string, T>();
  return (date: string): T => {
    if (!cache.has(date)) cache.set(date, read(date));
    return cache.get(date) as T;
  };
};

/** Sorts cards by date, parsing each distinct date once rather than twice per comparison. */
export const sortByDate = (cards: INextComponent[]) => {
  const unixMsOf = perDateString((date) => new EventrDate(date === "" ? undefined : date).getUnixMs);
  cards.sort((a, b) => unixMsOf(a.date) - unixMsOf(b.date) || 1);
};

export const getDateTimeISOFromEventrDateStringAndZone = (dateString: string, zoneType: TZoneTypes) => {
  const date = new EventrDate(dateString);

  switch (zoneType) {
    case "userLocal":
      return date.toUserLocalDateTimeString();
    case "itinLocal":
      return date.toItinLocalDateTimeString();
    case "utc":
      return date.dateTimeString;
    default:
      return date.toUserLocalDateTimeString();
  }
};

const getDateRanges = (start: EventrDate, end: EventrDate) => {
  const dates: EventrDate[] = [];
  const startDate = start.getCloneEventrDate();
  const endDate = end.getCloneEventrDate();

  for (
    const d = start.getCloneEventrDate();
    d.toItinLocalDateString() < endDate.toItinLocalDateString();
    d.add({ days: 1 })
  ) {
    if (d.toItinLocalDateString() !== startDate.toItinLocalDateString()) {
      const date = d.getCloneEventrDate();
      d.setFromObject({ hour: 0, minute: 0 });
      dates.push(date);
    }
  }

  return dates;
};

export const generateUpNextList = (
  wpUser: AppUser,
  data: any[],
  options: IRendererOptions,
  screenType: string,
  zoneType: TZoneTypes,
  sectionData?: V2,
  timeFormat?: string,
) => {
  if (!wpUser) return;

  const todos: ITodoList[] = [];

  if (sectionData?.sectionID === 8) {
    todos.push(...data.filter((item) => item.type === "todo"));
    data = data.filter((item) => item.type !== "todo" && item.type !== "stand");
  }

  const sortedPastPresent = filterEntitiesToPastAndPresentWP(data, wpUser, options.settings, screenType);

  // Entries share a few times between them, and each read parses the string and shifts its zone.
  const inPast = perDateString((date) => isDateInPast(date));
  const day = perDateString((date) => dayOf(new EventrDate(date), zoneType));
  const labelTime = perDateString((date) => formatTime(date, zoneType, timeFormat));
  const dateTimeISO = perDateString((date) => getDateTimeISOFromEventrDateStringAndZone(date, zoneType));

  const buildComponents = (list: any[]): INextComponent[] => {
    const comps: INextComponent[] = [];
    const entitiesWithChildren: any[] = [];

    if (options.settings.childEntity) {
      list.forEach((p) => {
        const children = JSON.parse(getV2ValueWP(options.settings.childEntity!, p));
        children.forEach((c: any) => {
          entitiesWithChildren.push({
            ...p,
            [options.settings.childEntity!]: c,
          });
        });
      });
    } else {
      entitiesWithChildren.push(...list);
    }

    entitiesWithChildren.forEach((p) => {
      let booking;

      if (options.settings.multiBooking) {
        if (sectionData?.sectionID === SectionId.HOTEL) {
          booking = hotelGetBooking(p, wpUser.id, p.identifier);
        } else {
          booking = tryParseJSON(getV2ValueWP(options.settings.multiBooking.locator, p, wpUser), []).find(
            (el: any) => el._id === p.identifier,
          );
        }
      }

      let time = booking
        ? booking[options.settings.multiBooking.primaryCard]
        : getV2ValueWP(options.settings.pastCriteria ?? "", p, wpUser);

      let time2 = booking
        ? booking[options.settings.multiBooking.secondaryCard]
        : getV2ValueWP(options.settings.secondaryCard ?? "", p, wpUser);

      const fetchCheckIn = getV2ValueWP("check_in", p, wpUser);
      const checkIn = fetchCheckIn === "" ? undefined : fetchCheckIn;

      if (options.settings.childEntityPastCriteria) {
        const childTime = getV2ValueWP(options.settings.childEntityPastCriteria, p);

        if (childTime !== "null") {
          time = combineChildTimeAndParent(time, childTime, options.settings.pastCriteriaJoiner as TPastCriteriaJoiner);
          time2 = combineChildTimeAndParent(
            time2,
            childTime,
            options.settings.pastCriteriaJoiner as TPastCriteriaJoiner,
          );
        }
      }

      if (time === "null") return;

      const isDiffDate = day(time) !== day(time2);

      const ongoing = options.settings.trackLive ? !inPast(time) && inPast(time2) : false;

      let selectedTime = time;

      if (!isDiffDate && !inPast(time2)) {
        selectedTime = time2;
      }

      if (checkIn && !isDiffDate && !inPast(checkIn)) {
        selectedTime = checkIn;
      }

      const isPast = inPast(selectedTime);
      const isUpcomingCheckIn = checkIn !== undefined && !inPast(checkIn);

      const labelPrefix = (() => {
        if (ongoing) return "Live";
        if (isDiffDate || isPast) return options.settings.primaryLabel;
        if (isUpcomingCheckIn) return "Check-in";
        return options.settings.secondaryLabel;
      })();

      comps.push({
        date: dateTimeISO(selectedTime),
        sortDate: selectedTime,
        data: {
          ...p,
          label: `${labelPrefix} ${labelTime(selectedTime)}`,
          live: ongoing,
          labelTime: selectedTime,
          startOrEnd: selectedTime === time ? "end" : "start",
        },
        renderOptions: sectionData ?? options,
      });

      if (options.settings.secondaryCard && isDiffDate) {
        const timeToUse = checkIn && !inPast(checkIn) ? checkIn : time2;

        comps.push({
          date: dateTimeISO(time2),
          sortDate: time2,
          data: {
            ...p,
            label: `${checkIn && !inPast(checkIn) ? "Check-in" : options.settings.secondaryLabel} ${labelTime(timeToUse)}`,
            labelTime: timeToUse,
            startOrEnd: "start",
          },
          renderOptions: sectionData ?? options,
        });
      }

      if (options.settings.multiDays && isDiffDate) {
        getDateRanges(new EventrDate(time2), new EventrDate(time)).forEach((date) => {
          comps.push({
            date: getDateTimeISOFromEventrDateStringAndZone(date.dateTimeString, zoneType),
            sortDate: date.dateTimeString,
            data: {
              ...p,
              subCard: true,
              multiDays: true,
            },
            renderOptions: sectionData ?? options,
          });
        });
      }
    });

    return comps;
  };

  const allComponents = buildComponents([...sortedPastPresent.present, ...sortedPastPresent.past]);

  const present: INextComponent[] = [];
  const past: INextComponent[] = [];
  const gracePeriod = getUserPref("account", "showPastItemsForInMins") ?? 0;
  const inPastAfterGrace = perDateString((date) => isDateInPast(date, gracePeriod));

  allComponents.forEach((c) => {
    if (inPastAfterGrace(c.sortDate)) {
      // Add custom choice
      past.push(c);
    } else {
      present.push(c);
    }
  });

  return {
    present,
    past,
    todos,
  };
};

// `identifier` is the room `_id` stamped on by filterEntitiesToPastAndPresentWP, which emits one
// entry per room the user is a guest in; without it every entry would resolve to the first room.
export const hotelGetBooking = (hotel: IEventrHotel, userId: number, identifier?: string) => {
  const booking =
    (identifier ? hotel.rooms?.find((b) => b._id === identifier) : undefined) ??
    hotel.rooms?.find((b) =>
      b.guests?.some((g) => {
        return g.option === userId;
      }),
    );
  if (!booking) {
    return null;
  }
  const c_booking = structuredCloneL(booking);
  if (!c_booking?.override) {
    if (hotel.defualtCheckIn && hotel.defualtCheckOut) {
      c_booking.checkIn = hotel.defualtCheckIn;
      c_booking.checkOut = hotel.defualtCheckOut;
    }
  }

  return c_booking;
};
